import { toast } from 'react-toastify'
import React, { useState, useEffect } from 'react'

import useAuth from 'store/AuthContext'
import NewsletterService from 'services/NewsletterService'
import UserTypeSection from './components/UserTypeSection'
import PersonalInfoSection from './components/PersonalInfoSection'
import PasswordChangeSection from './components/PasswordChangeSection'
import NewsletterPreferencesSection from './components/NewsletterPreferencesSection'

const ProfilePage = () => {
  const { user, editUser } = useAuth()
  const [isLegal, setIsLegal] = useState(user?.type === 'i')
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    companyName: user?.details?.company_name || '',
    companyId: user?.details?.company_code || '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordStrength, setPasswordStrength] = useState('')
  const [passwordMatch, setPasswordMatch] = useState('')
  const [saveStatus, setSaveStatus] = useState('idle')
  const [errors, setErrors] = useState({})
  const [newsletterLoading, setNewsletterLoading] = useState(true)
  const [newsletterPreferences, setNewsletterPreferences] = useState({
    subscribed_email: false,
    subscribed_sms: false,
  })

  useEffect(() => {
    if (user) {
      setIsLegal(user.type === 'i')
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        email: user.email || '',
        companyName: user.details?.company_name || '',
        companyId: user.details?.company_code || '',
        newPassword: '',
        confirmPassword: '',
      })
    }
  }, [user])

  useEffect(() => {
    NewsletterService.getPreferences()
      .then(data => {
        setNewsletterPreferences({
          subscribed_email: Boolean(data?.subscribed_email),
          subscribed_sms: Boolean(data?.subscribed_sms),
        })
      })
      .catch(() => {
        toast.error('შეტყობინებების პარამეტრების ჩატვირთვა ვერ მოხერხდა')
      })
      .finally(() => {
        setNewsletterLoading(false)
      })
  }, [])

  const handleInputChange = e => {
    const { name, value } = e.target

    if (name === 'phone') {
      let formattedValue = value.replace(/\D/g, '')
      if (formattedValue.length > 9) formattedValue = formattedValue.slice(0, 9)
      setFormData(prev => ({ ...prev, [name]: formattedValue }))
    } else if (name === 'companyId') {
      let formattedValue = value.replace(/\D/g, '')
      if (formattedValue.length > 9) formattedValue = formattedValue.slice(0, 9)
      setFormData(prev => ({ ...prev, [name]: formattedValue }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }

    if (name === 'newPassword') {
      checkPasswordStrength(value)
    }

    if (name === 'confirmPassword' || name === 'newPassword') {
      const newPass = name === 'newPassword' ? value : formData.newPassword
      const confirmPass =
        name === 'confirmPassword' ? value : formData.confirmPassword
      checkPasswordMatch(newPass, confirmPass)
    }
  }

  const checkPasswordStrength = password => {
    if (password.length === 0) {
      setPasswordStrength('')
      return
    }

    let strength = 0
    if (password.length >= 8) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    if (strength <= 2) {
      setPasswordStrength('weak')
    } else if (strength <= 4) {
      setPasswordStrength('medium')
    } else {
      setPasswordStrength('strong')
    }
  }

  const checkPasswordMatch = (newPassword, confirmPassword) => {
    if (confirmPassword.length === 0 && newPassword.length === 0) {
      setPasswordMatch('')
      return
    }

    if (newPassword === confirmPassword) {
      setPasswordMatch('match')
    } else {
      setPasswordMatch('no-match')
    }
  }

  const handleLegalToggle = () => {
    const newIsLegal = !isLegal
    setIsLegal(newIsLegal)
    if (!newIsLegal) {
      setFormData(prev => ({
        ...prev,
        companyName: '',
        companyId: '',
      }))
    }
  }

  const handleNewsletterToggle = (key, value) => {
    setNewsletterPreferences(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleSave = e => {
    e.preventDefault()
    setErrors({})

    const newErrors = {}
    if (!formData.name) newErrors.name = 'სახელის ველი სავალდებულოა.'
    if (!formData.phone) newErrors.phone = 'ტელეფონის ველი სავალდებულოა.'
    if (!formData.email) newErrors.email = 'ელ.ფოსტის ველი სავალდებულოა.'

    if (isLegal) {
      if (!formData.companyName)
        newErrors['details.company_name'] =
          'კომპანიის დასახელების ველი სავალდებულოა.'
      if (!formData.companyId)
        newErrors['details.company_code'] =
          'საიდენტიფიკაციო კოდის ველი სავალდებულოა.'
    }

    if (
      formData.newPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.new_password_confirmation = 'პაროლები არ ემთხვევა.'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaveStatus('saving')

    const data = new FormData()
    data.append('name', formData.name)
    data.append('phone', formData.phone)
    data.append('email', formData.email)
    data.append('type', isLegal ? 'i' : 'n')

    if (isLegal) {
      data.append('details[company_name]', formData.companyName)
      data.append('details[company_code]', formData.companyId)
    }

    if (formData.newPassword) {
      data.append('new_password', formData.newPassword)
      data.append('new_password_confirmation', formData.confirmPassword)
    }

    editUser(data)
      .then(() => NewsletterService.updatePreferences(newsletterPreferences))
      .then(() => {
        setSaveStatus('idle')
        toast.success('პროფილი წარმატებით განახლდა')
        setFormData(prev => ({
          ...prev,
          newPassword: '',
          confirmPassword: '',
        }))
        setPasswordMatch('')
        setPasswordStrength('')
      })
      .catch(err => {
        setSaveStatus('idle')
        if (err?.response?.data?.errors) {
          setErrors(err.response.data.errors)
        } else if (err?.response) {
          toast.error('პარამეტრების განახლება ვერ მოხერხდა')
        } else {
          toast.error('დაფიქსირდა გაუთვალისწინებელი შეცდომა')
        }
      })
  }

  console.log(errors.new_password)

  return (
    <form onSubmit={handleSave}>
      <div className="rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 pb-8 mb-8">
        <PersonalInfoSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        <UserTypeSection
          isLegal={isLegal}
          handleLegalToggle={handleLegalToggle}
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />
      </div>

      <NewsletterPreferencesSection
        preferences={newsletterPreferences}
        loading={newsletterLoading || saveStatus === 'saving'}
        onToggle={handleNewsletterToggle}
      />

      <PasswordChangeSection
        formData={formData}
        handleInputChange={handleInputChange}
        errors={errors}
        passwordStrength={passwordStrength}
        passwordMatch={passwordMatch}
      />

      <button
        type="submit"
        disabled={saveStatus === 'saving'}
        className="flex gap-3 justify-center items-center bg-citrus-red text-white py-3 rounded-xl text-[17px] font-semibold cursor-pointer transition-all duration-300 w-full sm:w-auto sm:min-w-48 hover:shadow-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed ml-auto mb-5 md:mb-0"
      >
        {saveStatus === 'saving' ? (
          <i className="fas fa-spinner animate-spin text-sm"></i>
        ) : (
          <>
            <i className="fas fa-save"></i>
            შენახვა
          </>
        )}
      </button>
    </form>
  )
}

export default ProfilePage
